import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import * as d3 from 'd3';

const baconColors = [
  '#EDEDE8', '#E4E4DE', '#D9D9D3', '#CECEC8', '#C4C4BE', '#BBBBB5',
];

const PAPER = '#F6F1E4';
const INK = '#1A1208';
const MEAT = '#C4452D';

const clip = (text, maxChars) => {
  if (text.length <= maxChars) return text;
  return `${text.slice(0, Math.max(2, maxChars - 1))}…`;
};

const nameLines = (name, screenR, fontPx) => {
  const words = String(name || '').split(/\s+/).filter(Boolean);
  if (words.length === 0 || screenR < 11) return [];

  const maxChars = Math.max(4, Math.floor((screenR * 1.78) / (fontPx * 0.52)));
  const maxLines = screenR < 26 ? 1 : screenR < 52 ? 2 : 3;

  if (words.length === 1 || maxLines === 1) {
    return [clip(words[0], maxChars)];
  }

  const lines = [clip(words[0], maxChars)];
  const rest = words.slice(1);
  let line = '';
  rest.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (next.length > maxChars && line && lines.length < maxLines) {
      lines.push(clip(line, maxChars));
      line = word;
    } else {
      line = next;
    }
  });
  if (line && lines.length < maxLines) {
    lines.push(clip(line, maxChars));
  } else if (line && lines.length) {
    lines[lines.length - 1] = clip(line, maxChars);
  }
  return lines;
};

const firstName = (value) => {
  const name = typeof value === 'string' ? value : value?.name;
  if (!name) return '';
  return name.trim().split(/\s+/)[0];
};

const sharedNameLines = (members, screenR, maxChars) => {
  if (screenR < 68) return [];
  const names = (members || []).map(firstName).filter(Boolean);
  if (names.length === 0) return [];
  const maxNames = screenR >= 110 ? 3 : 2;
  const shown = names.slice(0, maxNames).map((name) => clip(name, maxChars));
  const extra = names.length - shown.length;
  const line = shown.join(' · ');
  return extra > 0 ? [line, `+${extra}`] : [line];
};

const GrouplingsGraph = ({ data, onNodeClick, isMobile }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const zoomFrame = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [zoomK, setZoomK] = useState(1);
  const [hoveredId, setHoveredId] = useState(null);

  const groups = useMemo(() => {
    const list = [...(data?.relatedGroups || [])];
    list.sort((a, b) => {
      if (Boolean(a.isHub) !== Boolean(b.isHub)) return a.isHub ? -1 : 1;
      return (b.count || 0) - (a.count || 0);
    });
    return list;
  }, [data]);

  const dataKey = useMemo(
    () => groups.map((group) => `${group.id}:${group.count || 0}`).join('|'),
    [groups]
  );

  useEffect(() => {
    const updateDimensions = () => {
      if (!containerRef.current) return;
      const { width, height } = containerRef.current.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    const observer = new ResizeObserver(updateDimensions);
    if (containerRef.current) observer.observe(containerRef.current);
    window.addEventListener('resize', updateDimensions);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateDimensions);
    };
  }, [isMobile]);

  const nodes = useMemo(() => {
    const { width, height } = dimensions;
    if (!width || !height || groups.length === 0) return [];

    const inset = 10;
    const root = d3.hierarchy({ children: groups })
      .sum((d) => (d.children ? 0 : Math.max(1, d.count || 1)))
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    d3.pack()
      .size([Math.max(40, width - inset * 2), Math.max(40, height - inset * 2)])
      .padding(groups.length > 24 ? 4 : 7)(root);

    return root.leaves().map((leaf, index) => ({
      ...leaf.data,
      x: leaf.x + inset,
      y: leaf.y + inset,
      r: leaf.r,
      index,
    }));
  }, [groups, dimensions]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    if (!svg.node() || dimensions.width === 0) return;

    const zoom = d3.zoom()
      .scaleExtent([0.5, 14])
      .on('zoom', (event) => {
        svg.select('.bubbles-zoom').attr('transform', event.transform);
        cancelAnimationFrame(zoomFrame.current);
        zoomFrame.current = requestAnimationFrame(() => {
          setZoomK(event.transform.k);
        });
      });

    svg.call(zoom);
    svg.call(zoom.transform, d3.zoomIdentity);
    setZoomK(1);

    return () => {
      cancelAnimationFrame(zoomFrame.current);
      svg.on('.zoom', null);
    };
  }, [dimensions.width, dimensions.height, dataKey]);

  const handleEnter = useCallback((id) => {
    setHoveredId(id);
  }, []);

  const handleLeave = useCallback((id) => {
    setHoveredId((current) => (current === id ? null : current));
  }, []);

  const drawn = useMemo(() => {
    if (!hoveredId) return nodes;
    const rest = nodes.filter((group) => group.id !== hoveredId);
    const active = nodes.find((group) => group.id === hoveredId);
    return active ? [...rest, active] : nodes;
  }, [nodes, hoveredId]);

  if (groups.length === 0) return null;

  const k = Math.max(zoomK, 0.01);
  const screenFont = isMobile ? 12 : 13;

  return (
    <div
      ref={containerRef}
      className="bubbles-stage"
      style={{
        width: '100%',
        height: '100%',
        touchAction: 'none',
        position: 'relative',
      }}
    >
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        className="bubbles-svg"
      >
        <g className="bubbles-zoom">
          {drawn.map((group) => {
            const screenR = group.r * k;
            const fontSize = screenFont / k;
            const lines = nameLines(group.name, screenR, screenFont);
            const showCount = !group.isHub && screenR >= 44;
            const countLine = showCount ? `${group.count} shared` : null;
            const nameMax = Math.max(6, Math.floor((screenR * 1.5) / (screenFont * 0.5)));
            const memberLines = group.isHub ? [] : sharedNameLines(group.members, screenR, nameMax);
            const allLines = [
              ...lines,
              ...(countLine ? [countLine] : []),
              ...memberLines,
            ];
            const lineHeight = fontSize * 1.14;
            const block = allLines.length * lineHeight;
            const shared = group.count || 0;
            const memberNames = (group.members || [])
              .map((member) => (typeof member === 'string' ? member : member?.name))
              .filter(Boolean)
              .join(', ');
            const label = group.isHub
              ? `${group.name} — open details`
              : `${group.name} — ${shared} shared member${shared === 1 ? '' : 's'}${memberNames ? `: ${memberNames}` : ''}`;
            const bubbleFill = group.isHub ? PAPER : baconColors[group.index % baconColors.length];
            const lit = hoveredId === group.id;

            return (
              <g
                key={group.id || group.name}
                className={`bubble-node ${group.isHub ? 'is-hub' : ''} ${lit ? 'is-lit' : ''}`}
                transform={`translate(${group.x},${group.y})`}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => handleEnter(group.id)}
                onMouseLeave={() => handleLeave(group.id)}
                onClick={() => {
                  if (!group.id) return;
                  onNodeClick({
                    id: group.id,
                    name: group.name,
                    isHub: Boolean(group.isHub),
                  });
                }}
              >
                <title>{label}</title>
                <g className="bubble-visual">
                  <circle
                    r={group.r}
                    fill={bubbleFill}
                    stroke={group.isHub ? MEAT : INK}
                    strokeWidth={(group.isHub ? 3 : lit ? 2.2 : 1.4) / k}
                    opacity={group.isHub ? 1 : 0.92}
                  />
                  {allLines.length > 0 && (
                    <text
                      textAnchor="middle"
                      fill={INK}
                      fontFamily="'Karla Variable', Karla, sans-serif"
                      fontWeight={group.isHub ? 800 : 700}
                      fontSize={fontSize}
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {allLines.map((line, lineIndex) => {
                        const isCount = line === countLine;
                        const isMember = memberLines.includes(line);
                        return (
                          <tspan
                            key={`${lineIndex}-${line}`}
                            x="0"
                            y={-block / 2 + lineHeight * lineIndex + fontSize * 0.82}
                            fill={INK}
                            fontWeight={isCount || isMember ? 600 : group.isHub ? 800 : 700}
                            fontSize={isCount || isMember ? fontSize * 0.78 : fontSize}
                          >
                            {line}
                          </tspan>
                        );
                      })}
                    </text>
                  )}
                </g>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};

export default GrouplingsGraph;
